import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cva } from 'class-variance-authority';

// CVA: variante del botón de nivel en el popover
const levelButtonVariants = cva(
    'w-9 h-9 flex items-center justify-center rounded-lg text-xs font-bold border transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
    {
        variants: {
            state: {
                active_max:  'bg-violet-600 text-white border-violet-500 shadow-sm shadow-violet-500/30',
                active:      'bg-blue-600 text-white border-blue-500 shadow-sm shadow-blue-500/30',
                max:         'text-violet-400 border-gray-600 hover:bg-violet-900/30 hover:border-violet-500 hover:text-violet-300',
                default:     'text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500',
            },
        },
        defaultVariants: { state: 'default' },
    }
);

const SkillsRow_LevelCell = ({ item, skill, onLevelChange }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);

    const levels = [0, 1, 2, 3, 4, 5];
    const isMax = item.level === 5;

    const triggerLabel = isMax
        ? t('SkillsRow.label_max')
        : `${t('SkillsRow.label_lv')} ${item.level}`;

    const handleSelect = (lvl) => {
        onLevelChange(skill.Id, lvl);
        setOpen(false);
    };

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    className={`
                        inline-flex items-center justify-center gap-1.5
                        w-full max-w-[90px] px-2 py-1.5
                        rounded-lg border text-sm font-bold
                        transition-all duration-150
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                        ${isMax
                            ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600 hover:bg-violet-200 dark:hover:bg-violet-900/50'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }
                    `}
                    aria-label={`Nivel actual: ${triggerLabel}. Click para cambiar.`}
                >
                    <span>{triggerLabel}</span>
                    <ChevronDown
                        size={12}
                        className={`transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : 'rotate-0'}`}
                    />
                </button>
            </Popover.Trigger>

            <AnimatePresence>
                {open && (
                    <Popover.Portal forceMount>
                        <Popover.Content
                            side="bottom"
                            align="center"
                            sideOffset={6}
                            style={{ zIndex: 30 }}
                            asChild
                        >
                            <motion.div
                                initial={{ opacity: 0, y: -4, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -4, scale: 0.95 }}
                                transition={{ duration: 0.12, ease: 'easeOut' }}
                                className="
                                    bg-gray-800 dark:bg-gray-900
                                    border border-gray-600/50
                                    rounded-xl shadow-2xl shadow-black/40
                                    backdrop-blur-sm p-2
                                "
                            >
                                <div className="grid grid-cols-3 gap-1.5">
                                    {levels.map(lvl => {
                                        const isActive = item.level === lvl;
                                        const isMaxLvl = lvl === 5;

                                        const buttonState = isActive && isMaxLvl
                                            ? 'active_max'
                                            : isActive
                                                ? 'active'
                                                : isMaxLvl
                                                    ? 'max'
                                                    : 'default';

                                        return (
                                            <button
                                                key={lvl}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleSelect(lvl);
                                                }}
                                                className={levelButtonVariants({ state: buttonState })}
                                                aria-label={lvl === 5 ? 'Max' : `Nivel ${lvl}`}
                                            >
                                                {lvl === 5 ? 'M' : lvl}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Flecha del popover */}
                                <Popover.Arrow className="fill-gray-800 dark:fill-gray-900" />
                            </motion.div>
                        </Popover.Content>
                    </Popover.Portal>
                )}
            </AnimatePresence>
        </Popover.Root>
    );
};

export default SkillsRow_LevelCell;
